import {
  AGENT_IDS,
  DATA_CATALOG_API_TOOL_IDS,
  TaggingFlagProperty,
} from '@/app/data-catalog/_common/constants'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'
import { TagsStructure } from './use-table-details-query'

export type ColumnUpdate = {
  column_name: string
  column_description?: string
  business_definition?: string
  business_rules?: string
  tags?: TagsStructure
}

export type TaggingFlag = {
  property_name: TaggingFlagProperty
  property_value: 1
}

export type TableColumnUpdate = {
  table_name: string
  tagging_flag?: TaggingFlag
  columns: ColumnUpdate[]
}

export type ColumnUpdatePayload = {
  service_name: string
  database_name: string
  schema_name: string
  tables: TableColumnUpdate[]
}

export type ColumnUpdateResult = {
  column_name: string
  updated_fields: string[]
}

export type ColumnTableUpdateResult = {
  success: boolean
  table_fqn: string
  updated_columns: ColumnUpdateResult[]
  failed_columns: ColumnUpdateResult[]
}

export type SaveColumnResponse = {
  success: boolean
  updates: ColumnTableUpdateResult[]
}

export function useSaveColumnMutation() {
  const agentId = AGENT_IDS.DATA_CATALOG_AGENT
  const { apiRequest, domainId } = useApiRequest()

  return useMutation<SaveColumnResponse, Error, ColumnUpdatePayload>({
    mutationFn: async (params) => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })

      const payload = {
        columnUpdate: {
          service_name: params.service_name,
          database_name: params.database_name,
          schema_name: params.schema_name,
          tables: params.tables,
        },
      }

      const response = await apiRequest({
        path: basePath,
        queryParams: {
          tool_id: DATA_CATALOG_API_TOOL_IDS.UPDATE_COLUMN,
        },
        options: {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      })

      if (!response.ok) {
        throw new Error('Failed to save column data')
      }

      const json = (await response.json()) as { output: SaveColumnResponse }
      return json.output
    },
  })
}
